import { subMinutes } from "date-fns";
import { ActionableTrainSection } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/ActionableTrainSection";
import {
  ACTIONABLE_TRAINS_CSS_CONTAINER_NAME,
  aggregateTrainCount,
  groupAssistanceByEntryPoint,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/utils";
import { ADDITIONAL_FROM_TIME_MINUTES } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/constants";
import DecisionSupportContext from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/DecisionSupportContext";
import { useAlternativeTransportIncidentTasks } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportIncidentTasks";
import { useAssistanceInTimePeriod } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAssistanceInTimePeriod";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { FC, useContext, useEffect, useMemo } from "react";
import { Module } from "shared/components/Module";
import { Flex, tokens } from "shared/components/Spor";
import { ErrorContent } from "shared/components/states/ErrorContent";
import { LoadingContent } from "shared/components/states/LoadingContent";
import { isNotNullish } from "shared/utils/objectUtils";
import styled from "styled-components";

const ActionableTrainsWrapper = styled.div<{ $sectionNumber: number }>`
  width: 100%;

  display: flex;
  flex-flow: row wrap;

  container-name: ${ACTIONABLE_TRAINS_CSS_CONTAINER_NAME};
  container-type: inline-size;

  gap: ${tokens.default.size.spacing[2]};
`;

type ActionableTrainsProps = {
  event: InfrastructureResponse;
};

export const ActionableTrains: FC<ActionableTrainsProps> = ({
  event: { incidentId, countryCode },
}) => {
  const {
    horizonFromTime,
    horizonToTime,
    setTotalAffected,
    setAffectedWithWheelchair,
    setIsAffectedPending,
    setIsAffectedError,
  } = useContext(DecisionSupportContext);

  const startTime = useMemo(
    () => subMinutes(horizonFromTime, ADDITIONAL_FROM_TIME_MINUTES),
    [horizonFromTime],
  );

  const tasksResponse = useAlternativeTransportIncidentTasks(
    incidentId,
    startTime,
    horizonToTime,
  );

  const { data: assistanceData, status: assistanceStatus } =
    useAssistanceInTimePeriod(countryCode, startTime, horizonToTime);

  const groupedAssistance = useMemo(
    () =>
      assistanceData && tasksResponse.data
        ? groupAssistanceByEntryPoint(assistanceData, tasksResponse.data)
        : null,
    [assistanceData, tasksResponse.data],
  );

  useEffect(() => {
    if (tasksResponse.data) {
      // Sum up current passengers on ALL trains
      const { trainsAtIncident, trainsOutsideIncident } = tasksResponse.data;
      const insideCount = aggregateTrainCount(trainsAtIncident);
      const outsideCount = trainsOutsideIncident.reduce(
        (sum, ep) => sum + aggregateTrainCount(ep.trains),
        0,
      );
      setTotalAffected(insideCount + outsideCount);
    }
  }, [tasksResponse.data]);

  useEffect(() => {
    if (assistanceData) {
      // Count number of registered wheelchair events
      const wheelchairEvents = assistanceData.filter(
        (trainInfo) => String(trainInfo.type) === "TRAIN_WHEELCHAIR_PASSENGER",
      );

      if (wheelchairEvents.length > 0 && isNotNullish(tasksResponse.data)) {
        const allTrains = new Set(
          [
            ...tasksResponse.data.trainsAtIncident,
            ...tasksResponse.data.trainsOutsideIncident.flatMap(
              ({ trains }) => trains,
            ),
          ].map(({ id }) => `${id.countryCode}/${id.id}/${id.nominalDate}`),
        );

        const relevantWheelchairEvents = wheelchairEvents.filter(
          ({ trainIdentifier }) =>
            allTrains.has(
              `${trainIdentifier.countryCode}/${trainIdentifier.identifier}/${trainIdentifier.nominalDate}`,
            ),
        );

        setAffectedWithWheelchair(relevantWheelchairEvents.length);
      } else {
        setAffectedWithWheelchair(wheelchairEvents.length);
      }
    }
  }, [assistanceData, tasksResponse.data]);

  useEffect(() => {
    setIsAffectedPending(
      tasksResponse.status === "pending" || assistanceStatus === "pending",
    );
  }, [tasksResponse.status, assistanceStatus]);

  useEffect(() => {
    setIsAffectedError(
      tasksResponse.status === "error" || assistanceStatus === "error",
    );
  }, [tasksResponse.status, assistanceStatus]);

  if (tasksResponse.status === "pending") {
    return (
      <Module title="Påvirkede tog">
        <Flex height="400px">
          <LoadingContent size="lg" />
        </Flex>
      </Module>
    );
  }

  if (tasksResponse.status === "error" || !tasksResponse.data) {
    return (
      <Module title="Påvirkede tog">
        <Flex height="400px">
          <ErrorContent size="lg" />
        </Flex>
      </Module>
    );
  }

  const numberOfSections =
    tasksResponse.data.trainsOutsideIncident.length > 3
      ? 3
      : tasksResponse.data.trainsOutsideIncident.length + 1;

  return (
    <ActionableTrainsWrapper $sectionNumber={numberOfSections}>
      <ActionableTrainSection
        affectedTrains={tasksResponse.data.trainsAtIncident}
        assistanceEvents={groupedAssistance?.informationInside}
        title="Tog i bruddområdet"
        isCentreSection
      />
      {tasksResponse.data?.trainsOutsideIncident.map((entrypoint, index) => (
        <ActionableTrainSection
          key={entrypoint.entryPoint.reference.jbvId}
          title={`Tog på vei mot ${entrypoint.entryPoint.name ?? entrypoint.entryPoint.reference.jbvId}`}
          affectedTrains={entrypoint.trains}
          assistanceEvents={
            groupedAssistance?.stableOrderInformationOutside[index]
          }
        />
      ))}
    </ActionableTrainsWrapper>
  );
};
